import React, {
  useState,
  useContext,
  ChangeEvent,
  FormEvent,
  FocusEvent,
  JSX,
} from "react";
import { MdEmail } from "react-icons/md";
import { FaLock } from "react-icons/fa";
import { errorMessage } from "../common/ErrorMessageHandler";
import "./Login.scss";
import { TextInput } from "../common/TextInput";
import { userLogin, userRegister } from "../../apis/authApis";
import { setToken, setResponse } from "../../apis/util";
import { ProfileContext } from "../../contexts/APIProvider";
import Toast from "react-bootstrap/Toast";
import Register from "./Register";
import { PUBLIC_DOMAINS } from "../../constant/Constant";

const LOGIN_TAB_KEY = "login";
const REGISTER_TAB_KEY = "register";

interface LoginProps {
  handleClose: () => void;
}

interface LoginInputs {
  username: string;
  password: string;
}

interface RegisterInputs {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  companyName: string;
  Password: string;
  confirmPassword: string;
  referral: string;
  acceptMarketing: string;
  acceptPrivacy: string;
  country: string;
}

interface ErrorMessages {
  username: string;
  Password: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmPassword: string;
  referral: string;
}

interface TouchedFields {
  [key: string]: boolean;
}

interface ToastData {
  isShow: boolean;
  header: string;
  msg: string;
  toastType: "danger" | "success" | "warning" | "info" | "";
}

export function Login({ handleClose }: LoginProps): JSX.Element {
  const { setProfile, setLoggedIn } = useContext(ProfileContext);
  const [type, setType] = useState<string>(LOGIN_TAB_KEY);
  const [loginInputs, setLoginInputs] = useState<LoginInputs>({
    username: "",
    password: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastData>({
    isShow: false,
    header: "",
    msg: "",
    toastType: "",
  });
  const [registerInput, setRegisterInput] = useState<RegisterInputs>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    companyName: "",
    Password: "",
    confirmPassword: "",
    referral: "",
    acceptMarketing: "",
    acceptPrivacy: "",
    country: "",
  });

  const [showPassword, setShowPassword] = useState<{
    password: boolean;
    confirmPassword: boolean;
  }>({
    password: false,
    confirmPassword: false,
  });

  const [isTouched, setIsTouched] = useState<TouchedFields>({
    username: false,
    Password: false,
    firstName: false,
    lastName: false,
    email: false,
    companyName: false,
    confirmPassword: false,
    referral: false,
  });

  const [inputErrorMessage, setInputErrorMessage] = useState<ErrorMessages>({
    username: "",
    Password: "",
    firstName: "",
    lastName: "",
    email: "",
    confirmPassword: "",
    referral: "",
  });

  const [acceptMarketing, setAcceptMarketing] = useState<boolean>(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState<boolean>(false);

  const handleTabClick = (tabKey: string) => {
    setType(tabKey);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (type === LOGIN_TAB_KEY) {
      setLoginInputs((prev) => ({ ...prev, [name]: value }));
    } else {
      setRegisterInput((prev) => ({ ...prev, [name]: value }));
    }

    setInputErrorMessage((prev) => ({
      ...prev,
      [name]: errorMessage(name, value, registerInput),
    }));
  };

  const validateLoginForm = (): boolean => {
    const fieldNames = Object.keys(loginInputs) as (keyof LoginInputs)[];
    fieldNames.forEach((field) => {
      setInputErrorMessage((prev) => ({
        ...prev,
        [field]: errorMessage(field, loginInputs[field], registerInput),
      }));
    });

    return !inputErrorMessage?.username && !inputErrorMessage?.Password;
  };

  const isPublicEmail = (email: string): boolean => {
    if (!email.includes("@")) return false;
    const domain = email.split("@")[1];
    return PUBLIC_DOMAINS.includes(domain.toLowerCase());
  };

  const validateRegisterForm = (): boolean => {
    const fieldNames = Object.keys(registerInput) as (keyof RegisterInputs)[];
    fieldNames.forEach((field) => {
      setInputErrorMessage((prev) => ({
        ...prev,
        [field]: errorMessage(field, registerInput[field], registerInput),
      }));
    });

    const email = registerInput.email;
    if (email && isPublicEmail(email)) {
      setInputErrorMessage((prev) => ({
        ...prev,
        email: "Registration using public email domains is not allowed.",
      }));
      return false;
    }

    return (
      !inputErrorMessage?.firstName &&
      !inputErrorMessage?.lastName &&
      !inputErrorMessage?.email &&
      !inputErrorMessage?.Password &&
      !inputErrorMessage?.confirmPassword &&
      !inputErrorMessage?.username
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      if (type === LOGIN_TAB_KEY) {
        if (validateLoginForm()) {
          const response = await userLogin(
            loginInputs.username,
            loginInputs.password
          );
          setToken(response.token);
          setResponse(response);
          setProfile(response);
          setLoggedIn(true);
          handleClose();
        }
      } else if (validateRegisterForm()) {
        await userRegister({
          firstName: registerInput.firstName,
          lastName: registerInput.lastName,
          username: registerInput.username,
          email: registerInput.email,
          companyName: registerInput.companyName,
          country: registerInput.country,
          Password: registerInput.Password,
          confirmPassword: registerInput.confirmPassword,
          referral: registerInput.referral,
          acceptMarketing,
          acceptPrivacy
        });

        const registerLogin = await userLogin(
          registerInput.username,
          registerInput.Password
        );
        setToken(registerLogin.token);
        setResponse(registerLogin);
        setProfile(registerLogin);
        setLoggedIn(true);
        handleClose();
      }
    } catch (error: any) {
      setToast({
        isShow: true,
        header: type === LOGIN_TAB_KEY ? "LOGIN FAILED" : "ERROR OCCURRED",
        msg: error.message || "User Already Exists with similar credentials",
        toastType: "danger",
      });
    }
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setIsTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleEyeClick = (field: "password" | "confirmPassword") => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleLoginEyeClick = () => {
    setShowLoginPassword((prev) => !prev);
  };

  const handleToast = () => {
    setToast({ isShow: false, header: "", msg: "", toastType: "" });
  };

  const renderForm = () => {
    if (type === LOGIN_TAB_KEY) {
      return (
        <>
          <form onSubmit={handleSubmit}>
            <div className="tab-content">
              <div
                className="tab-pane fade in show active"
                id="login"
                role="tabpanel"
              >
                <div className="modal-body">
                  <TextInput
                    type="text"
                    label="Your username"
                    id="username"
                    name="username"
                    icon={<MdEmail className="icon" size={22} />}
                    value={loginInputs.username}
                    onChange={handleChange}
                    onBlur={handleInputBlur}
                    className="form-input"
                    errorText={
                      isTouched.username &&
                      loginInputs.username.trim().length === 0
                        ? "Please enter username"
                        : inputErrorMessage.username
                    }
                  />
                  <TextInput
                    type="password"
                    label="Your password"
                    id="password"
                    name="password"
                    showPassword={showLoginPassword}
                    handleEyeClick={handleLoginEyeClick}
                    icon={<FaLock className="icon" size={22} />}
                    value={loginInputs.password}
                    onChange={handleChange}
                    onBlur={handleInputBlur}
                    errorText={
                      isTouched.password &&
                      loginInputs.password.trim().length === 0
                        ? "Please enter password"
                        : inputErrorMessage.Password
                    }
                  />
                  <div className="text-center">
                    <button
                      className="btn btn-info waves-effect p-6 secondaryColor"
                      type="submit"
                    >
                      LOG IN<i className="bi bi-box-arrow-in-right"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div id="footerActions" className="modal-footer">
                <div className="options text-center text-md-right mt-1">
                  <p className="mb-1">
                    Not a member?{" "}
                    <button
                      type="button"
                      className="blue-text link-button"
                      onClick={() => handleTabClick(REGISTER_TAB_KEY)}
                    >
                      Sign Up
                    </button>
                  </p>
                </div>
                <button
                  className="btn btn-outline-info waves-effect ml-auto"
                  onClick={handleClose}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </form>
          <Toast
            className="toast-container"
            bg={toast.toastType}
            show={toast.isShow}
            onClose={handleToast}
          >
            {toast.header && (
              <Toast.Header>
                <strong className="me-auto">{toast.header}</strong>
              </Toast.Header>
            )}
            <Toast.Body className="toastBody">{toast.msg}</Toast.Body>
          </Toast>
        </>
      );
    } else {
      return (
        <Register
          handleSubmit={handleSubmit}
          registerInput={registerInput}
          setRegisterInput={setRegisterInput}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isTouched={isTouched}
          setIsTouched={setIsTouched}
          inputErrorMessage={inputErrorMessage}
          setInputErrorMessage={setInputErrorMessage}
          acceptMarketing={acceptMarketing}
          setAcceptMarketing={setAcceptMarketing}
          acceptPrivacy={acceptPrivacy}
          setAcceptPrivacy={setAcceptPrivacy}
          handleEyeClick={handleEyeClick}
          handleToast={handleToast}
          handleChange={handleChange}
          handleInputBlur={handleInputBlur}
          toast={toast}
        />
      );
    }
  };

  return (
    <div className="login-form">
      <div className="p-0">
        <div className="modal-content">
          <div className="modal-c-tabs">
            <ul className="nav nav-tabs md-tabs col-container">
              <li className="nav-item col p-0">
                <button
                  type="button"
                  onClick={() => handleTabClick(LOGIN_TAB_KEY)}
                  className={`nav-link w-100 ${
                    type === LOGIN_TAB_KEY ? "active" : ""
                  }`}
                >
                  Login <i className="bi bi-person-fill"></i>
                </button>
              </li>
              <li className="nav-item col p-0">
                <button
                  type="button"
                  onClick={() => handleTabClick(REGISTER_TAB_KEY)}
                  className={`nav-link w-100 ${
                    type === REGISTER_TAB_KEY ? "active" : ""
                  }`}
                >
                  Register
                </button>
              </li>
            </ul>
            {renderForm()}
          </div>
        </div>
      </div>
    </div>
  );
}
